import { FunctionComponent, ReactNode, useEffect, useState } from "react";
import { Dialog, DialogActions, DialogContent } from "@mui/material";

import { useTranslation } from "react-i18next";
import { Stack } from "@mui/system";

import { faClose } from "@fortawesome/pro-regular-svg-icons";

import { classNames } from "@/utils/classNames";

import { translateError, ERROR_TYPE } from "@/utils";

import { Button } from "../Button/Button";
import { ButtonColor } from "../Button/types";
import { Checkbox } from "../Checkbox/Checkbox";
import { Icon } from "../Icon/Icon";

interface ModalProps {
	children: ReactNode;
	title: string;
	subtitle?: string;
	isOpened: boolean;
	handleClose?: () => void;
	handleClear?: () => void;
	handleSave?: () => void;
	handleThirdSave?: () => void;
	handleSecondSave?: () => void;
	secondSubmitButtonText?: string;
	cancelButtonText?: string;
	thirdSubmitButtonText?: string;
	isLoading?: boolean;
	submitButtonText?: string;
	submitButtonColor?: string;
	clearButtonText?: string;
	clearButtonColor?: string;
	size?: "xs" | "sm" | "md" | "lg" | "xl";
	overflow?: boolean;
	checkbox?: boolean;
	handleCheckboxClick?: () => void;
	checkboxLabel?: string;
	disableSave?: boolean;
	aboveHeader?: ReactNode;
	submitButtonIcon?: JSX.Element;
	showActions?: boolean;
	dialogContentClassName?: string;
	saveButtonContainerClassName?: string;
	saveButtonClassName?: string;
	modalContainerClassName?: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	formError?: any;
}

export const Modal: FunctionComponent<ModalProps> = ({
	title,
	subtitle,
	children,
	isOpened,
	handleClose,
	handleSave,
	handleSecondSave,
	handleThirdSave,
	checkbox,
	handleCheckboxClick,
	secondSubmitButtonText,
	thirdSubmitButtonText,
	submitButtonText,
	submitButtonColor,
	isLoading = false,
	size = "xs",
	handleClear,
	clearButtonText,
	clearButtonColor,
	overflow,
	checkboxLabel,
	disableSave,
	aboveHeader,
	submitButtonIcon,
	showActions = true,
	dialogContentClassName = "",
	cancelButtonText,
	saveButtonContainerClassName,
	saveButtonClassName,
	modalContainerClassName,
	formError,
}) => {
	const { t } = useTranslation();
	const [error, setFormError] = useState<string>();

	const cssForOverflow = overflow
		? {
				"& .MuiDialogContent-root": {
					overflow: "visible",
				},
		  }
		: {};

	useEffect(() => {
		if (formError?.data?.message && typeof formError.data.message === "string") {
			setFormError(
				translateError[formError.data.message.toUpperCase() as unknown as ERROR_TYPE] ??
					t("basics.request-error")
			);
		} else if (formError?.data?.message && typeof formError.data.message === "object") {
			// Handle Zod validation errors that return an object
			setFormError(t("basics.request-error"));
		} else if (typeof formError === "string") {
			setFormError(formError);
		} else if (typeof formError === "object" && formError !== null) {
			const errorMessages = Object.values(formError)
				.map((fieldError) => {
					const { message } = fieldError as { message?: string };

					if (!message) {
						return null;
					}

					return translateError[message.toUpperCase() as unknown as ERROR_TYPE] ?? message;
				})
				.filter(Boolean) as string[];

			setFormError(errorMessages.join(", "));
		}
	}, [formError, t]);

	return (
		<Dialog
			fullWidth
			maxWidth={size}
			open={isOpened}
			sx={{
				overflow: "visible!important",
				"& .MuiDialog-paper": {
					borderRadius: "2xl",
					overflow: "visible",
				},
				"& .MuiDialogContent-root": {
					overflowY: "auto",
				},
				...cssForOverflow,
			}}
			onAbort={handleClose}
			onClose={handleClose ? handleClose : handleSave}
		>
			<Button
				className="bg-white !w-11 !h-11 !p-0 border-0 absolute !rounded-lg -top-11 -right-11"
				image={<Icon className="text-primary-gray w-5 h-5" icon={faClose} />}
				title=""
				onClick={() => (handleClose ? handleClose?.() : handleSave?.())}
			/>
			<div className={`!p-1 !bg-[#F7F8FB] !rounded-[20px] ${modalContainerClassName}`}>
				<div className="bg-white rounded-16 p-4 shadow-[0px_1px_2px_0px_rgba(28,39,76,0.12)]">
					{aboveHeader ? (
						<div
							className="w-[44px] h-[44px] flex items-center justify-center rounded-xl border-[0.28px] border-[#E6E6EC]"
							style={{
								boxShadow: "0px 1.38px 2.75px 0px #1018280F, 0px 1.38px 4.13px 0px #1018281A",
							}}
						>
							{aboveHeader}
						</div>
					) : null}
					{title && (
						<h2 className={"text-[20px] mt-3 font-semibold leading-6 text-primary-blue-dark"}>
							{title}
						</h2>
					)}
					{subtitle && (
						<p className="mt-[6px] text-primary-gray-lighter text-[14px] leading-5">
							{subtitle ?? ""}
						</p>
					)}

					<DialogContent className={`mt-6 custom-scrollbar !p-0 ${dialogContentClassName}`}>
						{children}
					</DialogContent>
				</div>
				{showActions && (
					<DialogActions sx={{ py: 1, overflow: "visible" }}>
						{handleCheckboxClick && typeof checkbox === "boolean" && (
							<div className="w-[150px] flex items-center">
								<Checkbox
									className="whitespace-nowrap"
									isChecked={checkbox}
									onChange={handleCheckboxClick}
								>
									{checkboxLabel}
								</Checkbox>
							</div>
						)}
						<div
							className={classNames(
								"w-full flex",
								"justify-between"
								// handleClear && handleSave ? "justify-between" : "justify-end"
							)}
						>
							{handleClear && (
								<div className="w-[150px]">
									<Button
										color={
											clearButtonColor
												? (clearButtonColor as ButtonColor)
												: ButtonColor.ACTION_SECONDARY
										}
										title={clearButtonText ?? t("basics.clear")}
										onClick={handleClear}
									/>
								</div>
							)}
							<Stack
								alignItems="center"
								className="w-full"
								direction="row"
								flexWrap="nowrap"
								justifyContent="center"
								marginTop={1}
								spacing={1}
							>
								{handleClose && (
									<div className="w-full">
										<Button
											className="!min-h-[44px]"
											color={ButtonColor.ACTION_SECONDARY}
											title={cancelButtonText ?? t("basics.cancel")}
											onClick={handleClose}
										></Button>
									</div>
								)}

								{handleSecondSave && (
									<div className="w-[220px]">
										<Button
											color={ButtonColor.ACTION_SECONDARY}
											isLoading={isLoading}
											title={secondSubmitButtonText ?? t("basics.saveChanges")}
											onClick={handleSecondSave}
										></Button>
									</div>
								)}

								{handleThirdSave && (
									<div className="w-[220px]">
										<Button
											color={ButtonColor.ACTION_SECONDARY}
											isLoading={isLoading}
											title={thirdSubmitButtonText ?? t("basics.saveChanges")}
											onClick={handleThirdSave}
										></Button>
									</div>
								)}

								{handleSave && (
									<div className={`w-full ${saveButtonContainerClassName ?? ""}`}>
										<Button
											className={`min-h-[44px] ${saveButtonClassName ?? ""}`}
											color={
												submitButtonColor ? (submitButtonColor as ButtonColor) : ButtonColor.ACTION
											}
											disabled={isLoading || disableSave}
											image={submitButtonIcon}
											isLoading={isLoading}
											title={submitButtonText ?? t("basics.saveChanges")}
											onClick={handleSave}
										></Button>
									</div>
								)}
							</Stack>
						</div>
					</DialogActions>
				)}
			</div>
			{error && (
				<p className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 text-[14px] text-[#FF1034]">
					{error}
				</p>
			)}
		</Dialog>
	);
};
